import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Api } from '@brokerage/utils/Api';

export const fetchMembers = createAsyncThunk(
  'members/fetchMembers',
  async (params, { getState, rejectWithValue }) => {
    try {
      const { agency_id, page = 1, search = '', forceRefresh = false } = params || {};

      const cacheKey = agency_id ? `${agency_id}_${page}_${search}` : `${page}_${search}`;

      const { membersCache } = getState().members;

      if (membersCache[cacheKey] && !forceRefresh) {
        return { ...membersCache[cacheKey], cacheKey, currentPage: page, searchQuery: search };
      }

      const apiParams = { page, per_page: 20, search };

      if (agency_id) apiParams.agency_id = agency_id;

      const { data, headers } = await Api.loadMembers(apiParams);
      const pagination = headers['x-pagination'] ? JSON.parse(headers['x-pagination']) : {};

      return {
        data,
        pagination,
        cacheKey,
        currentPage: page,
        searchQuery: search
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch members');
    }
  }
);

const initialState = {
  membersCache: {},
  members: [],
  pagination: {},
  loading: false,
  error: null,
  currentPage: 1,
  searchQuery: ''
};

const membersSlice = createSlice({
  name: 'members',
  initialState,
  reducers: {
    clearCache: state => {
      state.membersCache = {};
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchMembers.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMembers.fulfilled, (state, action) => {
        const { data, pagination, cacheKey, currentPage, searchQuery } = action.payload;

        state.membersCache[cacheKey] = { data, pagination };
        state.members = data;
        state.pagination = pagination;
        state.currentPage = currentPage;
        state.searchQuery = searchQuery;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchMembers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch members';
      });
  }
});

export const { clearCache, setSearchQuery, setCurrentPage } = membersSlice.actions;
export default membersSlice.reducer;
